import {
  computeLineInformation,
  DiffType
} from "react-diff-viewer-continued/lib/src/compute-lines"

/** A run of adjacent changed rows, addressed by row index in the rendered split view. */
export interface DiffHunk {
  startRow: number
  endRow: number
}

export interface DiffSummary {
  additions: number
  deletions: number
  hunks: DiffHunk[]
}

export const EMPTY_DIFF: DiffSummary = { additions: 0, deletions: 0, hunks: [] }

export const hasDiff = (summary: DiffSummary) => summary.hunks.length > 0

/**
 * Uses the viewer's own line computation, so row `i` here is the `i`-th `<tr>` the viewer
 * renders (split view, no title row, nothing folded) — that is what lets navigation scroll to it.
 */
export function summarizeDiff(oldText: string, newText: string): DiffSummary {
  if (oldText === newText) return EMPTY_DIFF

  const { lineInformation } = computeLineInformation(oldText, newText, true)
  const hunks: DiffHunk[] = []
  let additions = 0
  let deletions = 0

  lineInformation.forEach((line, row) => {
    const removed = line.left?.type === DiffType.REMOVED
    const added = line.right?.type === DiffType.ADDED
    if (removed) deletions++
    if (added) additions++
    if (!removed && !added) return

    const last = hunks[hunks.length - 1]
    if (last && last.endRow === row - 1) last.endRow = row
    else hunks.push({ startRow: row, endRow: row })
  })

  return { additions, deletions, hunks }
}

export function mergeDiffSummaries(
  summaries: ReadonlyArray<DiffSummary>
): DiffSummary {
  return summaries.reduce<DiffSummary>(
    (total, summary) => ({
      additions: total.additions + summary.additions,
      deletions: total.deletions + summary.deletions,
      hunks: [...total.hunks, ...summary.hunks]
    }),
    EMPTY_DIFF
  )
}
